import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  completeCalendarAuthorization,
  disconnectCalendar,
  fetchCalendarAuthorizationUrl,
  fetchCalendarConnection,
  fetchExternalCalendarEvents,
} from '@/api/calendarSync/calendarSyncApi';
import type {
  CalendarConnection,
  ExternalCalendarEvent,
} from '@/api/calendarSync/calendarSyncApi.types';
import {
  CALENDAR_CONNECTION_QUERY_KEY,
  EXTERNAL_CALENDAR_EVENTS_QUERY_KEY,
} from '@/constants/queryKeys';
import { useAuth } from '@/hooks/useAuth';
import { useHasWorkspaceModule } from '@/hooks/useHasWorkspaceModule';
import { MODULE_KEY } from '@/constants/modules';

interface CompleteAuthorizationVariables {
  code: string;
  state: string;
}

export function useCalendarConnectionQuery(): UseQueryResult<CalendarConnection> {
  const { status, activeScopeCode } = useAuth();
  return useQuery({
    queryKey: CALENDAR_CONNECTION_QUERY_KEY,
    queryFn: fetchCalendarConnection,
    enabled: status === 'authenticated' && activeScopeCode === 'tenant',
    retry: false,
  });
}

export function useExternalCalendarEventsQuery(
  fromIsoDate: string,
  toIsoDate: string
): UseQueryResult<ExternalCalendarEvent[]> {
  const { status, activeScopeCode } = useAuth();
  const hasCalendarSyncModule = useHasWorkspaceModule(MODULE_KEY.calendarSync);
  return useQuery({
    queryKey: [...EXTERNAL_CALENDAR_EVENTS_QUERY_KEY, fromIsoDate, toIsoDate],
    queryFn: function loadExternalEvents() {
      return fetchExternalCalendarEvents(fromIsoDate, toIsoDate);
    },
    enabled:
      status === 'authenticated' && activeScopeCode === 'tenant' && hasCalendarSyncModule === true,
    retry: false,
  });
}

export function useStartCalendarAuthorizationMutation(): UseMutationResult<string, Error, void> {
  return useMutation({ mutationFn: fetchCalendarAuthorizationUrl });
}

export function useCompleteCalendarAuthorizationMutation(): UseMutationResult<
  CalendarConnection,
  Error,
  CompleteAuthorizationVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function complete(variables: CompleteAuthorizationVariables) {
      return completeCalendarAuthorization(variables.code, variables.state);
    },
    onSuccess: function storeConnection(connection) {
      queryClient.setQueryData(CALENDAR_CONNECTION_QUERY_KEY, connection);
    },
  });
}

export function useDisconnectCalendarMutation(): UseMutationResult<CalendarConnection, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disconnectCalendar,
    onSuccess: function storeConnection(connection) {
      queryClient.setQueryData(CALENDAR_CONNECTION_QUERY_KEY, connection);
    },
  });
}
